import { createTheme } from '@mui/material/styles';

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS
// The single source of truth for FitSocial's visual language.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 4px base-unit spacing scale.
 * Usage: theme.spacing[4] => '16px'
 */
export const spacing = {
  0: '0px',
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
  16: '64px',
  20: '80px',
};

/**
 * Named layout constants so nothing is a magic number.
 */
export const layout = {
  sidebarWidth: '280px',
  sidebarCollapsedWidth: '72px',
  contentMaxWidth: '700px',
  widgetWidth: '340px',
  widgetMaxWidth: '340px',
  bottomNavHeight: '56px',
  headerHeight: '64px',
  dashboardMaxWidth: '1400px',
};

/**
 * Border radius tokens.
 */
export const radii = {
  none: '0px',
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  '2xl': '24px',
  full: '9999px',
};

/**
 * Shadow system.
 * Light mode uses conventional dark shadows; dark mode uses subtle colored glow.
 */
export const shadows = {
  light: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)',
    md: '0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
    lg: '0 12px 40px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.08)',
    xl: '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 20px rgba(0, 0, 0, 0.1)',
  },
  dark: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.3), 0 0px 8px rgba(17, 204, 17, 0.03)',
    md: '0 4px 12px rgba(0, 0, 0, 0.4), 0 0px 12px rgba(17, 204, 17, 0.05)',
    lg: '0 12px 40px rgba(0, 0, 0, 0.5), 0 0px 20px rgba(17, 204, 17, 0.06)',
    xl: '0 20px 60px rgba(0, 0, 0, 0.6), 0 0px 30px rgba(17, 204, 17, 0.08)',
  },
};

/**
 * Duration tokens (milliseconds).
 */
export const duration = {
  instant: '100ms',
  fast: '200ms',
  normal: '300ms',
  slow: '500ms',
  glacial: '1000ms',
};

/**
 * Easing tokens.
 */
export const easing = {
  easeOut: 'cubic-bezier(0.0, 0.0, 0.2, 1)',
  easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  spring: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
};

// ─────────────────────────────────────────────────────────────────────────────
// COLOR PALETTE
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Primary green palette (brand color #11cc11).
 */
export const green = {
  50: '#e6fce6',
  100: '#b3f5b3',
  200: '#80ee80',
  300: '#4de74d',
  400: '#26e126',
  500: '#11cc11', // brand primary
  600: '#0ea80e',
  700: '#0a850a',
  800: '#076207',
  900: '#034003',
};

/**
 * Secondary accent palette (electric blue - bold, complements green).
 */
export const blue = {
  50: '#eff6ff',
  100: '#dbeafe',
  200: '#bfdbfe',
  300: '#93c5fd',
  400: '#60a5fa',
  500: '#3B82F6', // accent
  600: '#2563eb',
  700: '#1d4ed8',
  800: '#1e40af',
  900: '#1e3a8a',
};

/**
 * Neutral palette for backgrounds, text, borders.
 */
const neutral = {
  0: '#FFFFFF',
  50: '#FAFAFA',
  100: '#F5F5F5',
  150: '#EEEEEE',
  200: '#E5E5E5',
  300: '#D4D4D4',
  400: '#A3A3A3',
  500: '#737373',
  600: '#525252',
  700: '#404040',
  750: '#2E2E2E',
  800: '#262626',
  850: '#1C1C1C',
  900: '#171717',
  950: '#0F0F0F',
};

/**
 * Achievement colors.
 */
export const achievement = {
  gold: '#FFD700',
  goldLight: '#FFF3B0',
  silver: '#C0C0C0',
  silverLight: '#E8E8E8',
  bronze: '#CD7F32',
  bronzeLight: '#F0D9B5',
};

/**
 * Workout type colors.
 */
export const workout = {
  strength: '#EF4444',
  strengthBg: 'rgba(239, 68, 68, 0.12)',
  cardio: '#F97316',
  cardioBg: 'rgba(249, 115, 22, 0.12)',
  flexibility: '#8B5CF6',
  flexibilityBg: 'rgba(139, 92, 246, 0.12)',
  hiit: '#EAB308',
  hiitBg: 'rgba(234, 179, 8, 0.12)',
};

/**
 * Streak gradient & colors.
 */
export const streak = {
  start: '#F97316',
  end: '#EF4444',
  gradient: 'linear-gradient(135deg, #F97316 0%, #EF4444 100%)',
};

/**
 * Nutrition macro colors.
 */
export const nutrition = {
  protein: '#3B82F6',
  proteinBg: 'rgba(59, 130, 246, 0.12)',
  carbs: '#EAB308',
  carbsBg: 'rgba(234, 179, 8, 0.12)',
  fat: '#EF4444',
  fatBg: 'rgba(239, 68, 68, 0.12)',
  fiber: '#22C55E',
  fiberBg: 'rgba(34, 197, 94, 0.12)',
};

// ─────────────────────────────────────────────────────────────────────────────
// TYPOGRAPHY
// ─────────────────────────────────────────────────────────────────────────────

export const fonts = {
  heading: "'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  brand: "'Ubuntu', sans-serif",
  mono: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
};

export const fontWeights = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
};

/**
 * Fluid type scale using clamp() for responsive sizing.
 * Format: clamp(min, preferred, max).
 */
export const typeScale = {
  display: {
    fontSize: 'clamp(2.5rem, 5vw, 4rem)',
    fontWeight: fontWeights.extrabold,
    lineHeight: 1.1,
    fontFamily: fonts.heading,
    letterSpacing: '-0.02em',
  },
  h1: {
    fontSize: 'clamp(2rem, 4vw, 2.75rem)',
    fontWeight: fontWeights.bold,
    lineHeight: 1.2,
    fontFamily: fonts.heading,
    letterSpacing: '-0.01em',
  },
  h2: {
    fontSize: 'clamp(1.5rem, 3vw, 2rem)',
    fontWeight: fontWeights.bold,
    lineHeight: 1.2,
    fontFamily: fonts.heading,
    letterSpacing: '-0.01em',
  },
  h3: {
    fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
    fontWeight: fontWeights.bold,
    lineHeight: 1.2,
    fontFamily: fonts.heading,
  },
  h4: {
    fontSize: 'clamp(1.125rem, 2vw, 1.5rem)',
    fontWeight: fontWeights.semibold,
    lineHeight: 1.3,
    fontFamily: fonts.heading,
  },
  h5: {
    fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
    fontWeight: fontWeights.semibold,
    lineHeight: 1.3,
    fontFamily: fonts.heading,
  },
  h6: {
    fontSize: '1rem',
    fontWeight: fontWeights.semibold,
    lineHeight: 1.4,
    fontFamily: fonts.heading,
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: fontWeights.medium,
    lineHeight: 1.5,
    fontFamily: fonts.body,
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: fontWeights.medium,
    lineHeight: 1.5,
    fontFamily: fonts.body,
  },
  body1: {
    fontSize: '1rem',
    fontWeight: fontWeights.regular,
    lineHeight: 1.5,
    fontFamily: fonts.body,
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: fontWeights.regular,
    lineHeight: 1.5,
    fontFamily: fonts.body,
  },
  caption: {
    fontSize: '0.75rem',
    fontWeight: fontWeights.regular,
    lineHeight: 1.5,
    fontFamily: fonts.body,
  },
  overline: {
    fontSize: '0.6875rem',
    fontWeight: fontWeights.semibold,
    lineHeight: 1.5,
    fontFamily: fonts.body,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// STYLED COMPONENTS THEMES (light + dark)
// ─────────────────────────────────────────────────────────────────────────────

const sharedTokens = {
  fonts,
  fontWeights,
  typeScale,
  spacing,
  layout,
  radii,
  duration,
  easing,
  achievement,
  workout,
  streak,
  nutrition,
  green,
  blue,
};

/**
 * Light theme for Styled Components.
 * Color contrast ratios verified for WCAG 2.1 AA compliance.
 */
export const lightTheme = {
  ...sharedTokens,
  mode: 'light',
  shadows: shadows.light,
  colors: {
    primary: green[500],
    primaryLight: green[100],
    primaryDark: green[700],
    primaryGhost: 'rgba(17, 204, 17, 0.08)',

    secondary: blue[500],
    secondaryLight: blue[100],
    secondaryDark: blue[700],

    background: '#F8FAF8',
    backgroundAlt: neutral[100],
    surface: '#FFFFFF',
    surfaceHover: neutral[50],
    surfaceActive: neutral[100],

    border: neutral[200],
    borderLight: neutral[150],

    text: neutral[900],
    textSecondary: neutral[600],
    textMuted: neutral[400],
    textOnPrimary: '#FFFFFF',
    textOnDark: '#FFFFFF',

    error: '#DC2626',
    errorLight: '#FEE2E2',
    warning: '#F59E0B',
    warningLight: '#FEF3C7',
    success: '#16A34A',
    successLight: '#DCFCE7',
    info: blue[500],
    infoLight: blue[100],

    heart: '#DC2626',
    online: '#22C55E',

    // Overlay / backdrop
    overlay: 'rgba(0, 0, 0, 0.5)',
    backdropBlur: 'rgba(248, 250, 248, 0.85)',

    // Skeleton
    skeletonBase: neutral[200],
    skeletonHighlight: neutral[100],
  },
};

/**
 * Dark theme for Styled Components.
 * Color contrast ratios verified for WCAG 2.1 AA compliance.
 */
export const darkTheme = {
  ...sharedTokens,
  mode: 'dark',
  shadows: shadows.dark,
  colors: {
    primary: green[400],
    primaryLight: 'rgba(17, 204, 17, 0.15)',
    primaryDark: green[600],
    primaryGhost: 'rgba(17, 204, 17, 0.06)',

    secondary: blue[400],
    secondaryLight: 'rgba(59, 130, 246, 0.15)',
    secondaryDark: blue[600],

    background: '#0C0D0C',
    backgroundAlt: neutral[950],
    surface: neutral[900],
    surfaceHover: neutral[850],
    surfaceActive: neutral[800],

    border: neutral[800],
    borderLight: neutral[850],

    text: neutral[50],
    textSecondary: neutral[400],
    textMuted: neutral[600],
    textOnPrimary: '#000000',
    textOnDark: '#FFFFFF',

    error: '#F87171',
    errorLight: 'rgba(239, 68, 68, 0.15)',
    warning: '#FBBF24',
    warningLight: 'rgba(245, 158, 11, 0.15)',
    success: '#4ADE80',
    successLight: 'rgba(34, 197, 94, 0.15)',
    info: blue[400],
    infoLight: 'rgba(59, 130, 246, 0.15)',

    heart: '#EF4444',
    online: '#4ADE80',

    // Overlay / backdrop
    overlay: 'rgba(0, 0, 0, 0.7)',
    backdropBlur: 'rgba(12, 13, 12, 0.85)',

    // Skeleton
    skeletonBase: neutral[800],
    skeletonHighlight: neutral[750],
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// MUI THEME FACTORY
// ─────────────────────────────────────────────────────────────────────────────

/**
 * MUI component overrides shared across light and dark themes.
 */
const getMuiOverrides = (customTheme) => ({
  MuiCssBaseline: {
    styleOverrides: {
      '*, *::before, *::after': {
        boxSizing: 'border-box',
      },
    },
  },

  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: fontWeights.semibold,
        fontFamily: fonts.body,
        borderRadius: radii.md,
        transition: `all ${duration.fast} ${easing.easeOut}`,
        '&:hover': {
          transform: 'scale(1.02)',
        },
        '&:active': {
          transform: 'scale(0.98)',
        },
      },
      containedPrimary: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: customTheme.mode === 'dark' ? shadows.dark.sm : shadows.light.sm,
          transform: 'scale(1.02)',
        },
      },
    },
    defaultProps: {
      disableElevation: true,
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: {
        transition: `all ${duration.fast} ${easing.easeOut}`,
        '&:focus-visible': {
          outline: `2px solid ${customTheme.colors.primary}`,
          outlineOffset: 2,
        },
        '&:hover': {
          transform: 'scale(1.08)',
        },
        '&:active': {
          transform: 'scale(0.95)',
        },
      },
    },
  },

  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: radii.lg,
        boxShadow: 'none',
        border: `1px solid ${customTheme.colors.border}`,
        backgroundImage: 'none',
        transition: `box-shadow ${duration.fast} ${easing.easeOut}, transform ${duration.fast} ${easing.easeOut}`,
        '&:hover': {
          boxShadow: customTheme.shadows.md,
        },
      },
    },
  },

  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: radii.md,
          transition: `border-color ${duration.fast} ${easing.easeOut}`,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: customTheme.colors.primary,
            borderWidth: '2px',
          },
        },
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: radii.full,
        fontWeight: fontWeights.medium,
        fontFamily: fonts.body,
      },
    },
  },

  MuiAvatar: {
    styleOverrides: {
      root: {
        fontFamily: fonts.body,
        fontWeight: fontWeights.semibold,
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: radii.xl,
        backgroundImage: 'none',
        boxShadow: customTheme.shadows.lg,
      },
      backdrop: {
        backdropFilter: 'blur(8px)',
        backgroundColor: customTheme.colors.overlay,
      },
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: radii.lg,
        border: `1px solid ${customTheme.colors.border}`,
        boxShadow: customTheme.shadows.lg,
        backgroundImage: 'none',
      },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: fontWeights.semibold,
        fontFamily: fonts.body,
        transition: `color ${duration.fast} ${easing.easeOut}`,
      },
    },
  },

  MuiTabs: {
    styleOverrides: {
      indicator: {
        borderRadius: radii.full,
        height: 3,
        transition: `all ${duration.normal} ${easing.spring}`,
      },
    },
  },

  MuiBottomNavigation: {
    styleOverrides: {
      root: {
        height: layout.bottomNavHeight,
        borderTop: `1px solid ${customTheme.colors.border}`,
        backgroundColor: customTheme.colors.surface,
      },
    },
  },

  MuiBottomNavigationAction: {
    styleOverrides: {
      root: {
        transition: `color ${duration.fast} ${easing.easeOut}`,
        '& .MuiTouchRipple-root': {
          display: 'none',
        },
        '&.Mui-selected': {
          color: customTheme.colors.primary,
        },
      },
    },
  },

  MuiAccordion: {
    styleOverrides: {
      root: {
        background: 'none',
        boxShadow: 'none',
        '&::before': {
          display: 'none',
        },
      },
    },
  },

  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        background: 'none',
      },
    },
  },

  MuiAccordionDetails: {
    styleOverrides: {
      root: {
        background: 'none',
      },
    },
  },

  MuiSkeleton: {
    styleOverrides: {
      root: {
        backgroundColor: customTheme.colors.skeletonBase,
      },
    },
    defaultProps: {
      animation: 'wave',
    },
  },

  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: radii.full,
        height: 6,
      },
    },
  },
});

/**
 * Create a MUI theme based on the current Styled Components theme.
 * @param {Object} customTheme - The active lightTheme or darkTheme object.
 * @returns {import('@mui/material/styles').Theme} A MUI theme instance.
 */
export const createMuiTheme = (customTheme) => {
  return createTheme({
    palette: {
      mode: customTheme.mode,
      primary: {
        main: customTheme.colors.primary,
        light: customTheme.mode === 'light' ? green[300] : green[200],
        dark: customTheme.mode === 'light' ? green[700] : green[600],
        contrastText: customTheme.colors.textOnPrimary,
      },
      secondary: {
        main: customTheme.colors.secondary,
        light: customTheme.mode === 'light' ? blue[300] : blue[200],
        dark: customTheme.mode === 'light' ? blue[700] : blue[600],
      },
      error: {
        main: customTheme.colors.error,
      },
      warning: {
        main: customTheme.colors.warning,
      },
      success: {
        main: customTheme.colors.success,
      },
      info: {
        main: customTheme.colors.info,
      },
      background: {
        default: customTheme.colors.background,
        paper: customTheme.colors.surface,
        border: customTheme.colors.border,
      },
      text: {
        primary: customTheme.colors.text,
        secondary: customTheme.colors.textSecondary,
        disabled: customTheme.colors.textMuted,
      },
      iconHover: {
        heart: customTheme.colors.heart,
        comment: customTheme.colors.secondary,
        general: customTheme.colors.primary,
      },
      divider: customTheme.colors.border,
    },
    typography: {
      fontFamily: fonts.body,
      fontWeightRegular: fontWeights.regular,
      fontWeightMedium: fontWeights.medium,
      fontWeightBold: fontWeights.bold,
      h1: { ...typeScale.h1 },
      h2: { ...typeScale.h2 },
      h3: { ...typeScale.h3 },
      h4: { ...typeScale.h4 },
      h5: { ...typeScale.h5 },
      h6: { ...typeScale.h6 },
      subtitle1: { ...typeScale.subtitle1 },
      subtitle2: { ...typeScale.subtitle2 },
      body1: { ...typeScale.body1 },
      body2: { ...typeScale.body2 },
      caption: { ...typeScale.caption },
      overline: { ...typeScale.overline },
    },
    shape: {
      borderRadius: 8,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1024,
        lg: 1200,
        xl: 1536,
      },
    },
    components: getMuiOverrides(customTheme),
  });
};
